import React from "react";
import styled from "@emotion/styled";
import UserImage from "@components/item/UserImage";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useApiRequest } from "@utils/ApiRequest";
import SwiperContainer from "@components/swiper/SwiperContainer";
import { SwiperSlide } from "swiper/react";
import { useNavigate } from "react-router-dom";
import { image } from "theme";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "0.4rem",
});
const InfoContainer = styled.div({
  display: "flex",
  justifyContent: "space-between",
  gap: "0.4rem",
  alignItems: "center",
  cursor: "pointer",
});

const LeftSide = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1.6rem",
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
  lineHeight: "2rem",
  width: "100%",
}));

const BrandBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  alignItems: "start",

  ".name": {
    fontSize: "1.2rem",
    color: theme.color.gray,
  },
}));

const LikeButton = styled.button`
  animation: ${({ liked }: { liked: boolean }) =>
    liked ? "bounce 0.3s" : "none"};

  @keyframes bounce {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }
`;

const ImageWrapper = styled(SwiperSlide)(({ theme }) => ({
  display: "flex",
}));

type Props = {
  id: number;
  name: string;
  koreanName: string;
  liked: boolean;
  src: string;
  withImage?: boolean;
  imageList?: string[];
  onClick?: () => void;
  brandSearch?: boolean;
};
const BrandCard = ({
  id,
  name,
  koreanName,
  liked,
  src,
  withImage,
  imageList,
  onClick,
  brandSearch,
}: Props) => {
  const { patchBrandLike } = useApiRequest();

  const navigate = useNavigate();

  const handleLike = () => {
    mutate();
  };

  const { mutate } = useMutation({
    mutationFn: () => patchBrandLike(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["brandList"],
      });
    },
  });

  const theme = useTheme();
  return (
    <Container>
      <InfoContainer>
        <LeftSide onClick={onClick ? onClick : () => navigate(`/brand/${id}`)}>
          <UserImage size="6rem" src={src} />
          <BrandBox>
            <span>{name}</span>
            <span className="name">{koreanName}</span>
          </BrandBox>
        </LeftSide>

        {brandSearch ? null : (
          <LikeButton onClick={handleLike} liked={liked}>
            <Image
              src={
                liked ? theme.image.like_full.default : theme.image.like.default
              }
            />
          </LikeButton>
        )}
      </InfoContainer>

      {withImage && (
        <SwiperContainer space={8}>
          {imageList?.map((item: any, index: number) => (
            <ImageWrapper key={index}>
              <Image src={item.thumbnail_url} width="18rem" height="11.6rem" />
            </ImageWrapper>
          ))}
        </SwiperContainer>
      )}
    </Container>
  );
};

export default BrandCard;
